"use client";

import * as React from "react";
import { Users as UsersIcon, MousePointerClick } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { cn } from "../lib/utils";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { getAgentAbbreviation } from "../lib/chatUtils";

type Assistant = {
  id: string;
  name?: string;
  agent_id?: string | number;
  email?: string; 
};

interface QuickAssignPopoverProps {
  trigger: React.ReactElement;
  assistants: Assistant[];
  disabledAssistantId?: string | null;
  sessionUserId?: string | null;
  onConfirm: (assistantId: string, note: string) => Promise<void> | void;
  onOpen?: () => void;
  isNotificationActive?: boolean;
}

let currentOpenPopover: (() => void) | null = null;

export const QuickAssignPopover: React.FC<QuickAssignPopoverProps> = ({
  trigger,
  assistants,
  disabledAssistantId,
  sessionUserId,
  onConfirm,
  onOpen,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [note, setNote] = React.useState("");
  const [side, setSide] = useState<"top" | "bottom" | "right">("right");
  const [align, setAlign] = useState<"start" | "center" | "end">("center");

  const insideClickRef = React.useRef(false);
  const triggerRef = React.useRef<HTMLElement>(null);
  
  const triggerWithRef = React.cloneElement(trigger, {
    ref: triggerRef,
    className: cn(
      trigger.props.className,
      open && "ring-1 ring-[#105BDB] outline-none"
    )
  });

  const close = () => {
    setOpen(false);
    setSelectedId(null);
    setNote("");
    if (currentOpenPopover === close) {
      currentOpenPopover = null;
    }
  };

  const handleAssign = async () => {
    if (!selectedId) return;
    await onConfirm(selectedId, note);
    close();
  };

  React.useEffect(() => {
    if (open && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const popoverHeight = 420; 
      const popoverWidth = 420;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      const spaceRight = viewportWidth - triggerRect.right;

      if (viewportHeight < 820) {
        if (spaceBelow >= popoverHeight) {
          setSide("bottom");
          setAlign("start");
        }
        else if (spaceAbove >= popoverHeight) {
          setSide("top");
          setAlign("start");
        }
        else if (spaceRight >= popoverWidth) {
          setSide("right");
          setAlign("center");
        }
        else {
          setSide("right");
          setAlign("center");
        }
      } else {
        if (spaceBelow >= popoverHeight) {
          setSide("bottom");
          setAlign("start");
        } else if (spaceAbove >= popoverHeight) {
          setSide("top");
          setAlign("start");
        } else if (spaceRight >= popoverWidth) {
          setSide("right");
          setAlign("center");
        } else {
          setSide("bottom");
          setAlign("start");
        }
      }
    }
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      if (currentOpenPopover && currentOpenPopover !== close) {
        currentOpenPopover();
      }
      currentOpenPopover = close;
      onOpen?.();
      setOpen(true);
    } else {
      if (!insideClickRef.current) {
        close();
      }
      insideClickRef.current = false;
    }
  };

  const deniaAgents = assistants.filter(a => {
    if (!a.email) return false;
    const emailMatch = a.email.match(/^hcbagente(\d{2})@hcb\.com$/);
    return emailMatch; 
  });

  const benidormAgents = assistants.filter(a => {
    if (!a.email) return true;
    const emailMatch = a.email.match(/^hcbagente(\d{2})@hcb\.com$/);
    return !emailMatch; 
  });

  const renderAgentsGrid = (agents: Assistant[], title?: string) => {
    if (agents.length === 0) return null;
    
    return (
      <>
        {title && (
          <div className="mt-4 mb-2">
            <h4 className="text-sm font-semibold text-[#4A5364] uppercase tracking-wide">
              {title}
            </h4>
          </div>
        )}
        <div className="grid grid-cols-5 gap-2">
          {agents.map((a) => {
            const abbreviation = getAgentAbbreviation(a?.name || "", false);
            const isSelected = selectedId === a;
            const isDisabled =
              disabledAssistantId &&
              String(disabledAssistantId) === String(a.id);

            return (
              <button
                key={a.id}
                type="button"
                disabled={!!isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedId(a);
                }}
                className={cn(
                  "inline-flex items-center justify-center gap-1 rounded-lg border p-2 text-sm",
                  isDisabled
                    ? "border-[#E2E4E9] text-[#A8B0BD] bg-[#F6F7F9] cursor-not-allowed"
                    : isSelected
                    ? "border-[#3B82F6] bg-[#EEF4FF] text-[#105BDB]"
                    : "border-[#E2E4E9] bg-white text-[#13161A] hover:bg-[#F6F7F9]"
                )}
              >
                <UsersIcon className="w-4 h-4" />
                <span>{abbreviation}</span>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerWithRef}</PopoverTrigger>

      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        avoidCollisions={false}
        collisionPadding={0}
        sticky="always"
        className="w-[420px] p-0 bg-white rounded-xl shadow-xl border border-[#E2E4E9] z-50"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownCapture={(e) => {
          insideClickRef.current = true;
          requestAnimationFrame(() => {
            insideClickRef.current = false;
          });
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          <h3 className="text-lg font-bold text-[#13161A]">{t("agents")}</h3>
          
          {renderAgentsGrid(benidormAgents, "BENIDORM")}
          
          {renderAgentsGrid(deniaAgents, "DENIA")}

          <textarea
            className="w-full mt-3 border border-[#E2E4E9] rounded-lg p-3 resize-none h-[90px] bg-white text-sm placeholder:text-[#6F7D95] focus:outline-none focus:ring-1 focus:ring-[#0064E0]"
            placeholder={t("additional_information_optional")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        <div className="flex justify-end gap-3 px-4 pb-4">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className="rounded-lg border border-[#D1D5DB] text-black px-4 py-2 hover:bg-gray-100"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleAssign();
            }}
            disabled={!selectedId}
            className={cn(
              "rounded-lg px-4 py-2 text-base font-medium",
              !selectedId
                ? "bg-[#EDEFF2] text-[#D5D9E0] cursor-not-allowed"
                : "bg-[#105BDB] text-white hover:bg-[#0055C4] active:bg-[#0049AE]"
            )}
          >
            {t("reasign_to_user")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
